<script setup lang="ts">
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { confirmDialog } from '~/composables/confirm'

const { isRevealed, confirm, cancel, onReveal } = confirmDialog

const title = ref('')
const description = ref()
const type = ref('info')

onReveal((data) => {
  type.value = data?.type || 'info'
  title.value =
    data?.title || (type.value === 'success' ? 'Success!' : 'Are you sure?')
  description.value = data?.description
})
</script>

<template>
  <TransitionRoot as="template" :show="isRevealed">
    <Dialog as="div" class="fixed z-30 inset-0 overflow-y-auto" @close="cancel">
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center md:block md:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 bg-secondary-900/50 backdrop-filter backdrop-blur-sm transition-opacity"
          />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden md:inline-block md:align-middle md:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
          enter-to="opacity-100 translate-y-0 md:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 md:scale-100"
          leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
        >
          <div
            class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all md:my-8 md:align-middle md:max-w-lg md:w-full min-w-xs md:p-6"
          >
            <div
              class="md:flex md:items-start"
              :class="{ 'md:items-center': !description }"
            >
              <div
                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 md:mx-0 md:h-10 md:w-10"
                :class="
                  {
                    success: 'bg-green-100',
                    info: 'bg-orange-100',
                    danger: 'bg-red-100',
                  }[type]
                "
              >
                <div
                  class="text-xl"
                  :class="
                    {
                      success: 'i-heroicons-outline-check text-green-600',
                      info: 'i-heroicons-outline-information-circle text-orange-600',
                      danger: 'i-heroicons-outline-exclamation text-red-600',
                    }[type]
                  "
                  aria-hidden="true"
                />
              </div>
              <div class="mt-3 text-center md:mt-0 md:ml-4 md:text-left">
                <DialogTitle
                  as="h3"
                  class="my-0 text-lg leading-6 font-medium text-gray-900"
                >
                  {{ title }}
                </DialogTitle>
                <div v-if="description" class="mt-2">
                  <p class="text-sm text-gray-500" v-html="description" />
                </div>
              </div>
            </div>
            <div
              class="mt-5 md:mt-4 flex-col md:flex md:flex-row-reverse gap-2"
            >
              <Button
                variant="primary"
                class="w-full md:w-auto"
                :danger="type === 'danger'"
                @click="confirm"
              >
                Confirm
              </Button>
              <Button class="mt-3 md:mt-0 w-full md:w-auto" @click="cancel">
                Cancel
              </Button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
